import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiPhone, FiMail, FiMapPin, FiClock, FiSend,
  FiUser, FiMessageSquare, FiSettings, FiCheck
} = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', phone: '', service: '', message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const services = [
    'Electrical Installation', 'CCTV Setup', 'Smart Home Automation',
    'Solar Power', 'Security Systems', 'Annual Maintenance',
    'Emergency Repair', 'Other'
  ];

  const contactInfo = [
    {
      icon: FiPhone,
      label: 'Phone / WhatsApp',
      value: '+91 99066 69918',
      link: 'tel:+919906669918',
      color: 'from-green-600 to-emerald-600'
    },
    {
      icon: FiMail,
      label: 'Email',
      value: 'tayyabelectricstudio@outlook.com',
      link: 'mailto:tayyabelectricstudio@outlook.com',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      icon: FiMapPin,
      label: 'Location',
      value: 'Fatehgarh, Baramulla, J&K',
      link: 'https://maps.google.com',
      color: 'from-red-600 to-orange-600'
    },
    {
      icon: FiClock,
      label: 'Working Hours',
      value: '24/7 Emergency Service',
      link: 'tel:+919906669918',
      color: 'from-purple-600 to-pink-600'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Call our own Netlify API (keeps keys safe)
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err?.error || 'Submission failed');
      }

      setSuccess(true);
      setFormData({ name: '', phone: '', service: '', message: '' });

      // Open WhatsApp after 1.5s
      setTimeout(() => {
        const msg =
          `👋 Hello Tayyab Electric Studio!\n\n` +
          `👤 Name: ${formData.name}\n` +
          `📞 Phone: ${formData.phone}\n` +
          `🔧 Service: ${formData.service}\n` +
          `💬 Message: ${formData.message}`;
        window.open(`https://wa.me/919906669918?text=${encodeURIComponent(msg)}`, '_blank');
      }, 1500);

    } catch (err) {
      console.error('Contact form error:', err);
      setError('Could not send message. Please try WhatsApp directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 md:pt-20 bg-black">

      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-blue-900/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-500/30 rounded-full blur-xl animate-pulse" />
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl animate-pulse" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300 mb-6"
          >
            <SafeIcon icon={FiPhone} className="text-lg" />
            <span>Get In Touch</span>
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="gradient-text">Contact</span>
            <span className="text-white"> Us</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question or need a quote? We're here to help 24/7.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.link}
                target={info.link.startsWith('http') ? '_blank' : '_self'}
                rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-gray-900/50 border border-gray-800 hover:border-blue-500/40 rounded-2xl p-6 text-center transition-all duration-300 hover:scale-105"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={info.icon} className="text-white text-xl" />
                </div>
                <p className="text-gray-400 text-xs mb-1">{info.label}</p>
                <p className="text-white font-semibold text-sm">{info.value}</p>
              </motion.a>
            ))}
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-900/50 border border-gray-800 rounded-3xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-2">Send us a Message</h2>
              <p className="text-gray-400 text-sm mb-8">We'll get back to you within 24 hours.</p>

              {/* Success */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-600/20 border border-green-500/40 rounded-2xl p-6 text-center mb-6"
                >
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiCheck} className="text-3xl text-green-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">Message Sent! ✅</h3>
                  <p className="text-gray-400 text-sm">WhatsApp is opening to confirm your message.</p>
                </motion.div>
              )}

              {/* Error */}
              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-red-600/20 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm mb-6"
                >
                  {error}
                </motion.div>
              )}

              {!success && (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name */}
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text" required placeholder="Your Name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div className="relative">
                    <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="tel" required placeholder="WhatsApp Number"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Service */}
                  <div className="relative">
                    <SafeIcon icon={FiSettings} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                    <select
                      required value={formData.service}
                      onChange={e => setFormData({ ...formData, service: e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors appearance-none"
                    >
                      <option value="">Select Service</option>
                      {services.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Message */}
                  <div className="relative">
                    <SafeIcon icon={FiMessageSquare} className="absolute left-4 top-4 text-gray-500" />
                    <textarea
                      required placeholder="Your Message"
                      rows={4}
                      value={formData.message}
                      onChange={e => setFormData({ ...formData, message: e.target.value })}
                      className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-3 text-white focus:border-blue-500 focus:outline-none transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-4 flex items-center justify-center space-x-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{loading ? 'Sending...' : 'Send Message'}</span>
                    {!loading && <SafeIcon icon={FiSend} className="group-hover:translate-x-1 transition-transform" />}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
